import { useEffect, useRef, useState } from 'react'
import { useRouteMatch } from 'react-router-dom'

import { ProjectManagerType } from './projectManager'
import { RoutePath } from '../../../router/routesConfig'
import { decryptionAES } from '../../../utilities/encryption'

import useWebService from '../../../services/web/useWebService'
import useLoading from '../../../control/loading/useLoading'
import useManageProjectStore from '../../../stores/manageProject/useManageProjectStore'
import useManageProjectAction from '../../../stores/manageProject/useManageProjectAction'
import useAppFolderAndTabStore from '../../../stores/appFolderAndTab/appFolderAndTabStore'
import useAppFolderAndTabAction from '../../../stores/appFolderAndTab/appFolderAndTabAction'
import * as Constants from '../../../constants/constants'
import constSubmittedType from '../../../constants/submittedType'
import { DialogType } from '../../../control/dialog/dialog'
import useTranslate from '../../../i18n/useTranslate'
import useFolderAndTab from './useFolderAndTab'
import * as RESWebServiceType from '../../../services/web/types/responseWebServiceTypes'
import constSubmittedStep from '../../../constants/submittedStep'
import { customFolder } from '../../../constants/constants'
import useHistoryAction from '../../../stores/history/useHistoryAction'
import submittedType from '../../../constants/submittedType'

type MatchParams = {
    projectID: string
    submittedID: string
}

const useProjectManager = (props: ProjectManagerType) => {
    const {} = props
    const matchPath = useRouteMatch<MatchParams>(RoutePath.manageProjectEdit)
    const encryptedProjectID = matchPath ? matchPath.params.projectID : null
    const projectID = encryptedProjectID ? decryptionAES(encryptedProjectID) : null
    const encryptedSubmittedID = matchPath ? matchPath.params.submittedID : null
    const submittedID = encryptedSubmittedID ? decryptionAES(encryptedSubmittedID) : null

    const manageProjectStore = useManageProjectStore()
    const manageProjectAction = useManageProjectAction()
    const appFolderAndTabStore = useAppFolderAndTabStore()
    const appFolderAndTabAction = useAppFolderAndTabAction()
    const historyStoreAction = useHistoryAction()

    const callWebService = useWebService()
    const loadingStore = useLoading()
    const folderAndTabHook = useFolderAndTab()

    const dialogRequestConsiderDocRef = useRef<any>()
    const [configDialog, setConfigDialog] =
        useState<Pick<DialogType, 'title' | 'content' | 'type' | 'onClickConfirm' | 'onAfterClose'>>()
    const dialogRef = useRef<Record<string, Function>>()
    const onRefDialog = (func: Record<string, Function>) => {
        //ห้าม set State หรือเรียก Action ในนี้
        dialogRef.current = func
    }
    const firstRender = useRef<boolean>(true)
    const appFolderAndTabStoreRef = useRef<typeof appFolderAndTabStore>()

    const translate = useTranslate()

    useEffect(() => {
        appFolderAndTabStoreRef.current = appFolderAndTabStore
    })

    useEffect(() => {
        if (firstRender.current && projectID && submittedID) {
            firstRender.current = false
            getProjectByID(Number(projectID), Number(submittedID))
        }
    }, [])

    useEffect(() => {
        return () => {
            manageProjectAction.clearStore()
            appFolderAndTabAction.clearStore()
        }
    }, [])

    const getMode = () => {
        let mode: 'CREATE' | 'EDIT' = 'CREATE'
        if (projectID && submittedID) {
            mode = 'EDIT'
        }

        return mode
    }

    const getProjectByID = async (projectID: number, submittedID: number) => {
        loadingStore.loadingOpen()
        const { result } = await callWebService.getProjectByID(projectID, submittedID)
        if (result) {
            manageProjectAction.setProjectData(result)
            historyStoreAction.updateDisplayHistory(submittedID)
        }
        loadingStore.loadingClose()

        return result
    }

    const isCustomFolder = (selectedFolderID: number) => {
        const lstCustomFolderID: Array<number> = Object.keys(Constants.customFolder).map((key) => {
            const lstCustomFolder = Constants.customFolder as any
            return Number(lstCustomFolder[key].id)
        })
        const isCustomFolder: boolean = lstCustomFolderID.indexOf(selectedFolderID ?? NaN) !== -1
        return isCustomFolder
    }

    const isProjectFilesUploadFolder = (selectedFolderID: number) => {
        return selectedFolderID === Constants.customFolder.projectFilesUpload.id
    }

    const getCustomFolderSubmitTypeInfo = (selectedFolder: any) => {
        switch (selectedFolder?.id) {
            case Constants.customFolder.closeProject.id:
                return {
                    value: constSubmittedType.closeProject,
                    nameTH: customFolder.closeProject.folderName_th,
                    nameEN: customFolder.closeProject.folderName_en,
                }
            case Constants.customFolder.projectPhase.id:
                return {
                    value: constSubmittedType.amendmentProject,
                    nameTH: customFolder.projectPhase.folderName_th,
                    nameEN: customFolder.projectPhase.folderName_en,
                }
            case Constants.customFolder.projectAmendment.id:
                return {
                    value: constSubmittedType.amendmentProject,
                    nameTH: customFolder.projectAmendment.folderName_th,
                    nameEN: customFolder.projectAmendment.folderName_en,
                }
            case Constants.customFolder.projectRenewal.id:
                return {
                    value: constSubmittedType.renewalProject,
                    nameTH: customFolder.projectRenewal.folderName_th,
                    nameEN: customFolder.projectRenewal.folderName_en,
                }
            case Constants.customFolder.terminateProject.id:
                return {
                    value: constSubmittedType.terminateProject,
                    nameTH: customFolder.terminateProject.folderName_th,
                    nameEN: customFolder.terminateProject.folderName_en,
                }
            case Constants.customFolder.adverseEvents.id:
                return {
                    value: constSubmittedType.adverseEvent,
                    nameTH: customFolder.adverseEvents.folderName_th,
                    nameEN: customFolder.adverseEvents.folderName_en,
                }
            case Constants.customFolder.raeProject.id:
                return {
                    value: constSubmittedType.nonconformance,
                    nameTH: customFolder.raeProject.folderName_th,
                    nameEN: customFolder.raeProject.folderName_en,
                }
            case Constants.customFolder.closeProjectByAdmin.id:
                return {
                    value: constSubmittedType.closeProjectByAdmin,
                    nameTH: customFolder.closeProjectByAdmin.folderName_th,
                    nameEN: customFolder.closeProjectByAdmin.folderName_en,
                }
            case Constants.customFolder.terminateProjectByAdmin.id:
                return {
                    value: constSubmittedType.terminateProjectByAdmin,
                    nameTH: customFolder.terminateProjectByAdmin.folderName_th,
                    nameEN: customFolder.terminateProjectByAdmin.folderName_en,
                }
            case Constants.customFolder.withdrawProject.id:
                return {
                    value: constSubmittedType.withdrawalProject,
                    nameTH: customFolder.withdrawProject.folderName_th,
                    nameEN: customFolder.withdrawProject.folderName_en,
                }
            case Constants.customFolder.withdrawSubmitted.id:
                return {
                    value: constSubmittedType.withdrawalSubmitted,
                    nameTH: customFolder.withdrawSubmitted.folderName_th,
                    nameEN: customFolder.withdrawSubmitted.folderName_en,
                }
            case Constants.customFolder.renewalAndAmendmentProject.id:
                return {
                    value: constSubmittedType.renewalAndAmendmentProject,
                    nameTH: customFolder.renewalAndAmendmentProject.folderName_th,
                    nameEN: customFolder.renewalAndAmendmentProject.folderName_en,
                }
            case Constants.customFolder.highRiskProjectProgressReport.id:
                return {
                    value: constSubmittedType.highRiskProjectProgressReport,
                    nameTH: customFolder.highRiskProjectProgressReport.folderName_th,
                    nameEN: customFolder.highRiskProjectProgressReport.folderName_en,
                }
            case Constants.customFolder.projectSiteVisit.id:
                return {
                    value: constSubmittedType.projectSiteVisit,
                    nameTH: customFolder.projectSiteVisit.folderName_th,
                    nameEN: customFolder.projectSiteVisit.folderName_en,
                }

            default:
                return null
        }
    }

    const loadCustomFolderData = async (selectedFolder: any) => {
        const projectID = manageProjectStore.projectData?.projectID
        const folderListLen = appFolderAndTabStore.folderList?.length ?? 0
        const latestFolderIdx = folderListLen - 1 > 0 ? 1 : 0
        const latestFolder = appFolderAndTabStore.folderList[latestFolderIdx] // example : [customFolder, serverFolder1, serverFolder2]
        const submittedID = latestFolder.submittedID
        if (projectID && submittedID) {
            loadingStore.loadingOpen()
            const lstFolder = appFolderAndTabStore.folderList.map((itm) => ({
                ...itm,
                isLastProcess: itm.id === selectedFolder.id,
            }))
            const updFolderAndTab = { ...appFolderAndTabStore, folderList: lstFolder, folderSelected: selectedFolder }
            appFolderAndTabAction.setState(updFolderAndTab)

            const response = await callWebService.getProjectByID(projectID, submittedID)
            const projectResult = response.result
            const submittedTypeInfo = getCustomFolderSubmitTypeInfo(selectedFolder) ?? projectResult.submitTypeInfo
            let customProjectRes: Partial<RESWebServiceType.GetProjectResultType> | undefined
            if (projectResult) {
                const KEEP_EXIST_DOCUMENT_FOLDER_SUBMITTED_TYPES = [
                    constSubmittedType.renewalAndAmendmentProject,
                    constSubmittedType.renewalProject,
                ]

                customProjectRes = {
                    ...projectResult,
                    submittedID: undefined,
                    stepID: constSubmittedStep.submit,
                    latestDocument: [],
                    documentInfo: KEEP_EXIST_DOCUMENT_FOLDER_SUBMITTED_TYPES.includes(selectedFolder.submittedTypeID)
                        ? projectResult.documentInfo
                        : [],
                    isFlowApproveWithComment: false,
                    synthesisApproveStatusInfo: undefined,
                    submitTypeInfo: submittedTypeInfo,
                }
            }

            manageProjectAction.setState({
                projectData: customProjectRes as RESWebServiceType.GetProjectResultType,
            })

            const isReloadProjectData = false
            const ignoreCustomFolder = false
            folderAndTabHook.loadTabAndFolder(
                customProjectRes as RESWebServiceType.GetProjectResultType,
                updFolderAndTab,
                isReloadProjectData,
                ignoreCustomFolder,
            )

            loadingStore.loadingClose()
        }
    }

    const loadProjectFilesUploadFolderData = () => {
        if (!manageProjectStore.projectData) return

        const updatedFolderList = appFolderAndTabStore.folderList.map((e) => ({
            ...e,
            isLastProcess: e.id === Constants.customFolder.projectFilesUpload.id,
        }))

        appFolderAndTabAction.setState({
            ...appFolderAndTabStore,
            folderList: updatedFolderList,
            folderSelected: Constants.customFolder.projectFilesUpload,
            tabRenderList: [],
            tabSelected: undefined,
        })

        manageProjectAction.setState({
            projectData: {
                ...manageProjectStore.projectData,
                submittedID: Constants.customFolder.projectFilesUpload.submittedID, // !Warning this could curse some error
                stepID: constSubmittedStep.submit,
                submitTypeInfo: {
                    value: constSubmittedType.projectFilesUpload,
                    nameTH: customFolder.projectFilesUpload.folderName_th,
                    nameEN: customFolder.projectFilesUpload.folderName_en,
                },
            },
        })
    }

    const changeFolderProcess = async (selectedFolder: any) => {
        const projectID = manageProjectStore.projectData?.projectID
        let submittedID = selectedFolder.submittedID

        if (isProjectFilesUploadFolder(selectedFolder.id)) {
            loadProjectFilesUploadFolderData()
        } else if (isCustomFolder(selectedFolder.id)) {
            loadCustomFolderData(selectedFolder)
        } else {
            if (projectID && submittedID) {
                const lstFolder = appFolderAndTabStore.folderList.map((itm) => ({
                    ...itm,
                    isLastProcess: itm.id === selectedFolder.id,
                }))
                const updFolderAndTab = {
                    ...appFolderAndTabStore,
                    folderList: lstFolder,
                    folderSelected: selectedFolder,
                }
                appFolderAndTabAction.setState(updFolderAndTab)
                const result = await getProjectByID(projectID, selectedFolder.submittedID)

                const isReloadProjectData = false
                folderAndTabHook.loadTabAndFolder(
                    result as RESWebServiceType.GetProjectResultType,
                    updFolderAndTab,
                    isReloadProjectData,
                )
            }
        }
    }

    const onClickRequestConsider = () => {
        //Click ยื่นเอกสารเพื่อพิจารณา
        dialogRequestConsiderDocRef.current?.open()
    }

    const manageClickActionFolder = (id: number) => {
        if (id === Constants.customFolder.docAdditional.id) {
            //Click ยื่นเอกสารเพื่อพิจารณา
            onClickRequestConsider()
        }
    }

    const handleClickFolder = (selectedFolder: any) => {
        const { folderSelected } = appFolderAndTabStore
        if (folderAndTabHook.isActionFolder(selectedFolder.id)) {
            manageClickActionFolder(selectedFolder.id)
        } else {
            if (selectedFolder.id === folderSelected?.id) return
            changeFolderProcess(selectedFolder)
        }
    }
    return {
        projectMode: getMode(),
        dialogRequestConsiderDocRef,
        configDialog,
        projectData: manageProjectStore.projectData,
        onClickFolder: handleClickFolder,
        onRefDialog: onRefDialog,
    }
}

export default useProjectManager
